// ============================================================
// QuamSpaceCanvas — shared creation engine surface.
//
// This is the canvas implementation for both:
//   - QuamSpace        (/square/workspace, scope='personal')
//   - Content Studio   (/square/projects/:id/studio, scope='project')
//
// The two surfaces mount this canvas via thin wrappers in
// routes/Quamspace/Index.tsx and routes/Projects/Studio.tsx.
// The wrappers own framing copy (workspace/memory vs project/runs)
// and pass live data + the Site Builder handoff seam.
//
// SCOPE OF THIS FILE
//   - Visual fidelity to docs/WORKSPACE_SPEC.md (chrome bar, mode
//     bar, mode-tinted accents, command bar, agent stream).
//   - 6 modes: document, pdf, presentation, spreadsheet, notes, build
//   - Local progressive-reveal demo state machine: idle → building → done
//   - Honest seam to Site Builder when Build mode finishes (Build
//     mode is generation; Site Builder is manual visual construction
//     and management — see docs/SITE_BUILDER_SPEC.md §boundary rule).
//
// WHAT IS NOT IN THIS FILE (deferred — see WORKSPACE_SPEC.md)
//   - Real /api/workspace/run dispatch from the canvas.
//     The Run button currently runs the local demo timeline.
//   - Realtime subscriptions to sq_workspace_sections and
//     sq_workspace_agent_events for live section/event ingestion.
//     The wrapper hooks (useStudioRuns, useWorkspaceEvents) already
//     subscribe at the page level and surface counts/history above
//     the canvas, but the canvas timeline itself is still local.
//   - File / Voice / Video / URL / Template attachments — visual chips
//     only; no upload, transcription, capture, fetch, or template pick.
//   - Real export generation. The "Export" buttons in the chrome bar
//     are visual posture only.
//
// This is intentional. The progressive reveal exists so the UX is
// reviewable end-to-end before realtime sections start landing in
// sq_workspace_sections. When real sections arrive, this file can
// swap its timeout-driven array for a Realtime subscription without
// touching the rest of the shell.
// ============================================================

import { useEffect, useRef, useState, type CSSProperties } from 'react'

// ----------------------------------------------------------------
// Types
// ----------------------------------------------------------------

export type CanvasModeId =
  | 'document'
  | 'pdf'
  | 'presentation'
  | 'spreadsheet'
  | 'notes'
  | 'build'

export type CanvasVariant = 'quamspace' | 'studio'

interface ModeDef {
  id: CanvasModeId
  label: string
  icon: string
  ext: string
  color: string
  shortcut: string
}

interface StatItem {
  value: string
  label: string
  color?: string
}

interface ExtractionItem {
  page: string
  label: string
  status: 'pass' | 'concern' | 'fail'
  detail?: string
}

interface SlideBullet {
  text: string
}

interface SlideStatItem {
  value: string
  label: string
}

interface SlideChartBar {
  label: string
  actual: number
  projected: number
}

interface ActionItem {
  text: string
  owner: string
  due: string
  status: 'urgent' | 'pending' | 'auto' | 'delegated'
}

interface SectionBase {
  type: string
  delay: number
  text?: string
  agent?: string
  color?: string
  // Per-type optional payloads
  stats?: StatItem[]
  items?: any[]
  bullets?: SlideBullet[]
  bars?: SlideChartBar[]
  ask?: { amount: string; segments: { label: string; pct: number; color: string }[] }
  table?: { headers: string[]; rows: (string | { value: string; key?: boolean })[][] }
  tags?: string[]
  noteItems?: { time: string; text: string; priority: 'high' | 'med' | 'low' }[]
  actionItems?: ActionItem[]
  subtext?: string
}

interface AgentMessage {
  t: number
  agent: string
  text: string
}

interface ModeContent {
  placeholder: string
  emptyTitle: string
  emptyBody: string
  sections: SectionBase[]
  agents: AgentMessage[]
}

export interface CanvasLiveContext {
  // Real backend data the wrapper has loaded for this surface.
  // Surfaced as honest counters in the chrome and right footer.
  // The progressive reveal timeline itself is still local demo.
  runsCount?: number
  agentEventsCount?: number
  loading?: boolean
  error?: string | null
  latestRunStatus?: string | null
  latestRunMode?: string | null
}

export interface QuamSpaceCanvasProps {
  variant: CanvasVariant
  projectId?: string
  initialMode?: CanvasModeId
  // Wrapper-supplied live data. Does not drive the local timeline,
  // only the chrome counters / right footer counters.
  live?: CanvasLiveContext
  // Honest Site Builder handoff seam. Wired only by the Studio
  // wrapper (project context exists). When undefined the canvas
  // renders the seam in a disabled "requires project" posture so
  // QuamSpace users see why Build mode cannot publish from a
  // personal workspace.
  onOpenInSiteBuilder?: () => void
  // Real workspace-run dispatch seam. When provided, the canvas's
  // Run button calls this with the current prompt + mode BEFORE the
  // local timeline plays so the wrapper can create a real
  // sq_workspace_runs row through /api/workspace/run. The wrapper
  // returns the created (workspace_id, run_id) pair so the canvas
  // can bind a live render path through useLiveRun(). The canvas
  // does not block on the result — the local agent timeline still
  // plays as visual feedback while the real run streams sections.
  onRequestRun?: (params: {
    prompt: string
    mode: CanvasModeId
  }) => Promise<{ workspaceId: string; runId: string } | null> | void
  // Real backend-driven render. When non-null the canvas overlays
  // a LIVE indicator and renders the real persisted sections from
  // sq_workspace_sections instead of (or in addition to) the local
  // demo content. The wrapper supplies this via useLiveRun().
  liveRun?: {
    runId: string | null
    workspaceId: string | null
    status: string | null
    /** Mode of the live run (document, presentation, build, …). */
    mode?: string | null
    /** Provider that produced this run (gamma, internal, mock, …). */
    provider?: string | null
    sections: Array<{
      id: string
      section_order: number
      section_type: string
      title: string | null
      content_json: Record<string, unknown>
      source_agent: string | null
    }>
    events: Array<{
      id: string
      agent_name: string
      event_type: string
      message: string
      created_at: string
    }>
    error: string | null
  } | null
}

// ----------------------------------------------------------------
// Mode definitions
// ----------------------------------------------------------------

const MODES: ModeDef[] = [
  { id: 'document',     label: 'Document', icon: '◼', ext: '.docx', color: '#2563eb', shortcut: 'D' },
  { id: 'pdf',          label: 'PDF',      icon: '◆', ext: '.pdf',  color: '#7c3aed', shortcut: 'P' },
  { id: 'presentation', label: 'Slides',   icon: '▶', ext: '.pptx', color: '#dc2626', shortcut: 'S' },
  { id: 'spreadsheet',  label: 'Sheets',   icon: '▦', ext: '.xlsx', color: '#059669', shortcut: 'X' },
  { id: 'notes',        label: 'Notes',    icon: '✎', ext: '.md',   color: '#f59e0b', shortcut: 'N' },
  { id: 'build',        label: 'Build',    icon: '◧', ext: '.site', color: '#14b8a6', shortcut: 'B' },
]

// Agent palette — fixed regardless of mode.
const AGENT_COLORS: Record<string, string> = {
  ATHENA:   '#0891b2',
  MORPHEUS: '#f59e0b',
  OBSIDIAN: '#dc2626',
  APOLLO:   '#2563eb',
  AURA:     '#ec4899',
  ORACLE:   '#059669',
  ECHO:     '#2563eb',
}

const ALL_AGENTS = ['ATHENA', 'MORPHEUS', 'OBSIDIAN', 'APOLLO', 'AURA', 'ORACLE', 'ECHO']
const ALL_SOURCES = ['Strategy Intel', 'Finance', 'Silver Key DB', 'Legal', 'Operations']

// ----------------------------------------------------------------
// Demo content per mode
//
// All section/agent timing is local-only. When realtime ingestion
// lands these arrays will be replaced by Supabase subscriptions to
// sq_workspace_sections and sq_workspace_agent_events.
// ----------------------------------------------------------------

const DEMO_CONTENT: Record<CanvasModeId, ModeContent> = {
  document: {
    placeholder: "e.g. Draft a partnership agreement between Square Code and Silver Key Realty…",
    emptyTitle: 'Create Document',
    emptyBody: 'Type a request and the agents will assemble a structured document section by section.',
    sections: [
      { type: 'title', delay: 400, text: 'Strategic Partnership Agreement' },
      { type: 'meta',  delay: 700, text: 'Prepared by Square Code · 2026-04-08 · CONFIDENTIAL' },
      { type: 'divider', delay: 900 },
      { type: 'heading', delay: 1100, text: '1 · Parties' },
      { type: 'body', delay: 1400, agent: 'ATHENA', color: AGENT_COLORS.ATHENA,
        text: 'This Agreement is entered into between Square Code (the “Operator”) and Silver Key Realty Group (the “Partner”) for the purpose of joint go-to-market under the Silver Key brand within the Las Vegas metro.' },
      { type: 'heading', delay: 2000, text: '2 · Scope of Engagement' },
      { type: 'body', delay: 2300, agent: 'ATHENA', color: AGENT_COLORS.ATHENA,
        text: 'Square Code will provide an operator workspace, automated client intake, and a sealed audit trail through OLIVIUM. Silver Key will provide deal flow, market data, and licensed agent coverage.' },
      { type: 'heading', delay: 3100, text: '3 · Financial Terms' },
      { type: 'stats', delay: 3400, agent: 'MORPHEUS', color: AGENT_COLORS.MORPHEUS,
        stats: [
          { value: '15%', label: 'Operator share' },
          { value: '$25k', label: 'Setup' },
          { value: '24mo', label: 'Term' },
          { value: '90d', label: 'Out clause' },
        ]},
      { type: 'heading', delay: 4300, text: '4 · Security & Compliance' },
      { type: 'security', delay: 4600, agent: 'OBSIDIAN', color: AGENT_COLORS.OBSIDIAN,
        text: 'All client data is held under OLIVIUM RLS with sealed audit trails. Service-role keys never leave the server boundary. Section 6 grants the Partner read-only audit visibility, not raw data access.' },
      { type: 'callout', delay: 5400, agent: 'ORACLE', color: AGENT_COLORS.ORACLE,
        text: 'Recommend pre-signing review by both parties’ counsel and a 14-day pilot before clause 7 (revenue split) activates.' },
    ],
    agents: [
      { t: 350,  agent: 'ATHENA',   text: 'reading partnership intent…' },
      { t: 900,  agent: 'ATHENA',   text: 'drafted Parties section' },
      { t: 1500, agent: 'ATHENA',   text: 'pulled Silver Key org metadata' },
      { t: 2200, agent: 'MORPHEUS', text: 'modeling 24-month revenue split' },
      { t: 3300, agent: 'MORPHEUS', text: 'inserted financial terms grid' },
      { t: 4100, agent: 'OBSIDIAN', text: 'reviewing data sharing posture' },
      { t: 4700, agent: 'OBSIDIAN', text: 'flagged audit visibility scope' },
      { t: 5200, agent: 'AURA',     text: 'adjusting type rhythm + grid' },
      { t: 5500, agent: 'ORACLE',   text: 'final recommendation drafted' },
    ],
  },

  pdf: {
    placeholder: 'e.g. Analyze the Henderson property inspection PDF and extract findings…',
    emptyTitle: 'Create PDF Analysis',
    emptyBody: 'Drop a PDF or describe one to extract findings and produce a structured analysis.',
    sections: [
      { type: 'title',   delay: 400, text: 'Henderson Property — Inspection Analysis' },
      { type: 'meta',    delay: 700, text: 'Source: hendersonInspection_2026-03-22.pdf · 14 pages' },
      { type: 'divider', delay: 900 },
      { type: 'heading', delay: 1100, text: 'Extracted Findings' },
      { type: 'extraction', delay: 1400, agent: 'ATHENA', color: AGENT_COLORS.ATHENA,
        items: [
          { page: 'p.2',  label: 'Roof condition',     status: 'pass' },
          { page: 'p.4',  label: 'Foundation cracks',  status: 'concern', detail: 'hairline, NW corner' },
          { page: 'p.6',  label: 'HVAC inspection',    status: 'pass' },
          { page: 'p.8',  label: 'Electrical panel',   status: 'fail',    detail: 'pre-1990 FPE' },
          { page: 'p.10', label: 'Plumbing tree',      status: 'pass' },
          { page: 'p.12', label: 'Termite report',     status: 'concern', detail: 'prior treatment, no recurrence' },
        ]},
      { type: 'heading', delay: 3000, text: 'Critical Findings' },
      { type: 'body', delay: 3300, agent: 'OBSIDIAN', color: AGENT_COLORS.OBSIDIAN,
        text: 'The pre-1990 Federal Pacific electrical panel is the only finding with safety implications. Replacement should be a non-negotiable closing condition; market rate $2.8k–$4.2k.' },
      { type: 'heading', delay: 4000, text: 'Cost-Benefit' },
      { type: 'stats', delay: 4300, agent: 'MORPHEUS', color: AGENT_COLORS.MORPHEUS,
        stats: [
          { value: '$3.5k', label: 'Panel replace' },
          { value: '$1.2k', label: 'Foundation seal' },
          { value: '$0',    label: 'HVAC' },
          { value: '$4.7k', label: 'Total ask' },
        ]},
      { type: 'callout', delay: 5400, agent: 'ORACLE', color: AGENT_COLORS.ORACLE,
        text: 'Recommend negotiating $5,000 credit at close — covers all concern + fail items with operator margin.' },
    ],
    agents: [
      { t: 350,  agent: 'ATHENA',   text: 'parsing PDF text layer…' },
      { t: 900,  agent: 'ATHENA',   text: 'segmented 14 pages' },
      { t: 1500, agent: 'ATHENA',   text: 'extracted 6 findings rows' },
      { t: 2400, agent: 'OBSIDIAN', text: 'cross-checked safety codes' },
      { t: 3200, agent: 'OBSIDIAN', text: 'flagged FPE panel' },
      { t: 3900, agent: 'MORPHEUS', text: 'pricing remediation items' },
      { t: 4400, agent: 'MORPHEUS', text: 'cost-benefit grid built' },
      { t: 5000, agent: 'ORACLE',   text: 'computed negotiation envelope' },
      { t: 5500, agent: 'ECHO',     text: 'final summary written' },
    ],
  },

  presentation: {
    placeholder: 'e.g. Build the Silver Key investor pitch deck…',
    emptyTitle: 'Create Slides',
    emptyBody: 'Describe the deck. Slides are generated server-side via Gamma when wired. Local preview only for now.',
    sections: [
      { type: 'slide-title', delay: 400, text: 'Silver Key Realty · Series Seed' },
      { type: 'slide', delay: 1100, text: 'Problem',
        bullets: [{ text: 'Las Vegas resale market is opaque' }, { text: 'Operators waste 40%+ of cycle on intake' }, { text: 'No audit trail across vendors' }] },
      { type: 'slide', delay: 2000, text: 'Solution',
        bullets: [{ text: 'Square Code operator workspace' }, { text: 'OLIVIUM sealed audit trail' }, { text: 'Project-scoped Content Studio' }] },
      { type: 'slide-stats', delay: 2900, text: 'Traction',
        stats: [
          { value: '14', label: 'Pilot operators' },
          { value: '$1.2M', label: 'GMV ytd' },
          { value: '92%', label: 'NPS' },
          { value: '0', label: 'Audit failures' },
        ]},
      { type: 'slide-chart', delay: 3800, text: 'Revenue Trajectory',
        bars: [
          { label: 'Q1', actual: 38, projected: 0 },
          { label: 'Q2', actual: 62, projected: 0 },
          { label: 'Q3', actual: 0,  projected: 78 },
          { label: 'Q4', actual: 0,  projected: 96 },
        ]},
      { type: 'slide-ask', delay: 4900, text: 'The Ask',
        ask: { amount: '$1.5M', segments: [
          { label: 'Engineering', pct: 60, color: '#2563eb' },
          { label: 'GTM',         pct: 25, color: '#f59e0b' },
          { label: 'Reserve',     pct: 15, color: '#059669' },
        ]}},
    ],
    agents: [
      { t: 350,  agent: 'ATHENA',   text: 'reading positioning deck…' },
      { t: 1000, agent: 'ATHENA',   text: 'drafted problem slide' },
      { t: 1900, agent: 'ECHO',     text: 'rewrote solution copy' },
      { t: 2700, agent: 'ORACLE',   text: 'pulled traction metrics' },
      { t: 3300, agent: 'MORPHEUS', text: 'modeling revenue trajectory' },
      { t: 3900, agent: 'MORPHEUS', text: 'projected Q3/Q4 bars' },
      { t: 4500, agent: 'AURA',     text: 'tuned chart visual rhythm' },
      { t: 4950, agent: 'ATHENA',   text: 'composed investment ask' },
      { t: 5300, agent: 'AURA',     text: 'deck ready for review' },
    ],
  },

  spreadsheet: {
    placeholder: 'e.g. Build a Henderson acquisition model with three scenarios…',
    emptyTitle: 'Create Spreadsheet',
    emptyBody: 'Describe the model and the agents will assemble a structured analysis grid.',
    sections: [
      { type: 'title',   delay: 400, text: 'Henderson Acquisition Model' },
      { type: 'meta',    delay: 700, text: '3-scenario sensitivity · 60 month horizon' },
      { type: 'divider', delay: 900 },
      { type: 'spreadsheet-table', delay: 1300, agent: 'MORPHEUS', color: AGENT_COLORS.MORPHEUS,
        table: {
          headers: ['Line', 'Conservative', 'Base', 'Aggressive'],
          rows: [
            ['Purchase price',   '$420,000', '$420,000', '$420,000'],
            ['Down payment',     '$84,000',  '$84,000',  '$84,000'],
            ['Rehab budget',     '$22,000',  '$28,000',  '$36,000'],
            ['ARV',              '$510,000', '$540,000', '$575,000'],
            ['Hold (months)',    '8',        '6',        '4'],
            ['Carry cost',       '$11,200',  '$8,400',   '$5,600'],
            ['Sale fees (6%)',   '$30,600',  '$32,400',  '$34,500'],
            [{ value: 'Net cash flow', key: true }, { value: '$58,200', key: true }, { value: '$71,200', key: true }, { value: '$85,300', key: true }],
            [{ value: 'Cash-on-cash', key: true }, { value: '69%', key: true }, { value: '85%', key: true }, { value: '102%', key: true }],
            ['Risk score',       'low',      'medium',   'medium-high'],
          ],
        }},
      { type: 'heading', delay: 3200, text: 'Sensitivity' },
      { type: 'body', delay: 3500, agent: 'ORACLE', color: AGENT_COLORS.ORACLE,
        text: 'Cash-on-cash returns are most sensitive to ARV (±$15k = ±9 pts) and hold period (each extra month removes ~2.4 pts). Rehab budget within $6k either side is noise.' },
      { type: 'stats', delay: 4300, agent: 'MORPHEUS', color: AGENT_COLORS.MORPHEUS,
        stats: [
          { value: '$487k', label: 'Breakeven ARV' },
          { value: '11.2mo', label: 'Breakeven hold' },
          { value: '92%',    label: 'Confidence' },
          { value: '$71.2k', label: 'Base net' },
        ]},
    ],
    agents: [
      { t: 350,  agent: 'MORPHEUS', text: 'pulling comps + carry rates…' },
      { t: 900,  agent: 'MORPHEUS', text: 'building base scenario' },
      { t: 1500, agent: 'MORPHEUS', text: 'building aggressive scenario' },
      { t: 2100, agent: 'MORPHEUS', text: 'building conservative scenario' },
      { t: 2700, agent: 'ORACLE',   text: 'running sensitivity sweep' },
      { t: 3400, agent: 'ORACLE',   text: 'identified ARV as primary driver' },
      { t: 4000, agent: 'MORPHEUS', text: 'computed breakeven envelope' },
      { t: 4600, agent: 'OBSIDIAN', text: 'risk scoring complete' },
      { t: 5000, agent: 'ECHO',     text: 'model ready' },
    ],
  },

  notes: {
    placeholder: 'e.g. Capture the Erica Henderson client meeting…',
    emptyTitle: 'Create Notes',
    emptyBody: 'Capture a meeting or thought. The agents tag, prioritize, and route action items.',
    sections: [
      { type: 'title', delay: 400, text: 'Erica Henderson · Discovery Call' },
      { type: 'meta',  delay: 700, text: '2026-04-08 · 32 minutes · in-person' },
      { type: 'note-tag', delay: 900, tags: ['client', 'henderson', 'inspection', 'silver-key'] },
      { type: 'divider', delay: 1100 },
      { type: 'note-item', delay: 1400, noteItems: [
        { time: '00:02', text: 'Erica wants to close before May 15 — hard deadline tied to relocation', priority: 'high' },
      ]},
      { type: 'note-item', delay: 1900, noteItems: [
        { time: '00:08', text: 'Inspection report has FPE panel concern — Erica unaware until now', priority: 'high' },
      ]},
      { type: 'note-item', delay: 2400, noteItems: [
        { time: '00:14', text: 'Silver Key has comparable property at $445k — flag as backup', priority: 'med' },
      ]},
      { type: 'note-item', delay: 2900, noteItems: [
        { time: '00:21', text: 'Financing pre-approved through Wells, no contingencies', priority: 'low' },
      ]},
      { type: 'note-item', delay: 3400, noteItems: [
        { time: '00:26', text: 'Erica prefers async updates — Slack > email > phone', priority: 'low' },
      ]},
      { type: 'note-item', delay: 3900, noteItems: [
        { time: '00:30', text: 'Schedule follow-up after seller responds to credit ask', priority: 'med' },
      ]},
      { type: 'heading', delay: 4400, text: 'Action items' },
      { type: 'action-items', delay: 4700, agent: 'ATHENA', color: AGENT_COLORS.ATHENA,
        actionItems: [
          { text: 'Send seller the $5k credit ask in writing', owner: 'Jordan', due: '2026-04-09', status: 'urgent' },
          { text: 'Pull comparable property details from Silver Key DB', owner: 'APOLLO',  due: '2026-04-09', status: 'auto' },
          { text: 'Draft async update template for Erica', owner: 'ECHO',  due: '2026-04-10', status: 'pending' },
          { text: 'Block calendar for follow-up call once response lands', owner: 'Jordan', due: '2026-04-12', status: 'delegated' },
        ]},
    ],
    agents: [
      { t: 350,  agent: 'ECHO',     text: 'transcribing call audio…' },
      { t: 1000, agent: 'ECHO',     text: 'drafted timestamped note items' },
      { t: 1700, agent: 'ATHENA',   text: 'tagging entities + topics' },
      { t: 2300, agent: 'ATHENA',   text: 'priority assignment per item' },
      { t: 2900, agent: 'OBSIDIAN', text: 'flagged FPE concern as high' },
      { t: 3500, agent: 'APOLLO',   text: 'cross-referenced Silver Key DB' },
      { t: 4100, agent: 'ATHENA',   text: 'extracting action items' },
      { t: 4700, agent: 'ORACLE',   text: 'routing assignments' },
      { t: 5100, agent: 'ECHO',     text: 'capture sealed' },
    ],
  },

  build: {
    placeholder: 'e.g. Generate a landing page for Silver Key Realty…',
    emptyTitle: 'Create Site Draft',
    emptyBody: 'Build mode is the generation lane. The agents propose pages, blocks, and a style direction. Manual visual construction lives in Site Builder.',
    sections: [
      { type: 'title',   delay: 400, text: 'Silver Key Realty · Landing Page' },
      { type: 'meta',    delay: 700, text: 'Generated draft · 1 site · 4 pages · 7 blocks' },
      { type: 'divider', delay: 900 },
      { type: 'heading', delay: 1100, text: 'Site Architecture' },
      { type: 'body', delay: 1400, agent: 'ATHENA', color: AGENT_COLORS.ATHENA,
        text: 'Single-site, four-page structure: Home, Listings, About, Contact. Home anchors three top-fold blocks (hero, value, listings preview) and an inquiry CTA in the footer.' },
      { type: 'page-outline', delay: 2000, agent: 'ATHENA', color: AGENT_COLORS.ATHENA,
        items: [
          { path: '/',         title: 'Home',     blocks: 5 },
          { path: '/listings', title: 'Listings', blocks: 1 },
          { path: '/about',    title: 'About',    blocks: 4 },
          { path: '/contact',  title: 'Contact',  blocks: 2 },
        ]},
      { type: 'heading', delay: 2700, text: 'Hero Block' },
      { type: 'hero-preview', delay: 3000, agent: 'AURA', color: AGENT_COLORS.AURA,
        text: 'Las Vegas resale, on operator rails.', items: ['Trusted by 14 operators', '0 audit failures', 'Sealed by OLIVIUM'] },
      { type: 'heading', delay: 3700, text: 'Feature Grid' },
      { type: 'feature-grid', delay: 4000, agent: 'AURA', color: AGENT_COLORS.AURA,
        items: [
          { title: 'Sealed Audit', body: 'Every action timestamped + chained.' },
          { title: 'Operator Workspace', body: 'Documents, sheets, slides, notes.' },
          { title: 'Realtime Comms',  body: 'Slack, email, in-app — one envelope.' },
          { title: 'Vendor Routing', body: 'Inspections, lenders, escrow, in line.' },
        ]},
      { type: 'heading', delay: 4700, text: 'Style Direction' },
      { type: 'style-direction', delay: 5000, agent: 'AURA', color: AGENT_COLORS.AURA,
        items: [
          { label: 'Palette',    value: 'obsidian + teal accent' },
          { label: 'Type',       value: 'editorial display + mono labels' },
          { label: 'Density',    value: 'high information, low decoration' },
          { label: 'Motion',     value: 'subtle reveal, no parallax' },
        ]},
      { type: 'cta-block', delay: 5700, agent: 'ECHO', color: AGENT_COLORS.ECHO,
        text: 'Talk to an operator', subtext: 'Inquiry form posts straight into the project Comms channel.' },
    ],
    agents: [
      { t: 350,  agent: 'ATHENA',   text: 'planning site information architecture…' },
      { t: 1100, agent: 'ATHENA',   text: 'fixed 4 pages, 7 blocks total' },
      { t: 1900, agent: 'AURA',     text: 'composing hero block direction' },
      { t: 2700, agent: 'AURA',     text: 'building feature grid copy' },
      { t: 3400, agent: 'AURA',     text: 'visual style direction locked' },
      { t: 4100, agent: 'ECHO',     text: 'wrote CTA block copy' },
      { t: 4700, agent: 'OBSIDIAN', text: 'reviewed inquiry routing posture' },
      { t: 5200, agent: 'ATHENA',   text: 'site draft assembled' },
      { t: 5600, agent: 'ORACLE',   text: 'ready for handoff to Site Builder' },
    ],
  },
}

// ----------------------------------------------------------------
// Main canvas component
// ----------------------------------------------------------------

type BuildState = 'idle' | 'building' | 'done'

export default function QuamSpaceCanvas(props: QuamSpaceCanvasProps) {
  const { variant, initialMode = 'document', live, onOpenInSiteBuilder, onRequestRun, liveRun } = props

  const [mode, setMode] = useState<CanvasModeId>(initialMode)
  const [buildState, setBuildState] = useState<BuildState>('idle')
  const [visibleSections, setVisibleSections] = useState<number[]>([])
  const [visibleMsgs, setVisibleMsgs] = useState<number[]>([])
  const [prompt, setPrompt] = useState('')
  const [elapsed, setElapsed] = useState(0)

  const timeoutsRef = useRef<number[]>([])
  const tickerRef = useRef<number | null>(null)
  const canvasScrollRef = useRef<HTMLDivElement>(null)
  const agentScrollRef = useRef<HTMLDivElement>(null)

  const modeDef = MODES.find((m) => m.id === mode)!
  const accent = modeDef.color
  const content = DEMO_CONTENT[mode]

  // Reset on mode switch
  useEffect(() => {
    resetState()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach((id) => clearTimeout(id))
      if (tickerRef.current) window.clearInterval(tickerRef.current)
    }
  }, [])

  // Auto-scroll canvas
  useEffect(() => {
    const el = canvasScrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [visibleSections.length])

  // Auto-scroll agent panel
  useEffect(() => {
    const el = agentScrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [visibleMsgs.length])

  // Real-run state sync — when the wrapper-supplied liveRun resolves
  // to a terminal status, flip the canvas out of the building state
  // so the operator stops seeing the local building shimmer once
  // truth has landed. We never override an in-flight local timeline
  // upward (e.g. real status="running" + local "done") so the local
  // demo timeline can still finish gracefully if the realtime path
  // is slower than the local clock.
  useEffect(() => {
    if (!liveRun) return
    if (liveRun.status === 'done' && buildState !== 'done') {
      setBuildState('done')
      if (tickerRef.current) {
        window.clearInterval(tickerRef.current)
        tickerRef.current = null
      }
    } else if (liveRun.status === 'error' && buildState !== 'idle') {
      // Error stops the build, but we leave the local sections
      // visible so the operator can read them. The error itself is
      // surfaced through the LiveSectionsOverlay below.
      if (tickerRef.current) {
        window.clearInterval(tickerRef.current)
        tickerRef.current = null
      }
    }
  }, [liveRun, buildState])

  function resetState() {
    timeoutsRef.current.forEach((id) => clearTimeout(id))
    timeoutsRef.current = []
    if (tickerRef.current) {
      window.clearInterval(tickerRef.current)
      tickerRef.current = null
    }
    setBuildState('idle')
    setVisibleSections([])
    setVisibleMsgs([])
    setElapsed(0)
    setPrompt('')
  }

  function startBuild() {
    if (buildState === 'building') return
    setBuildState('building')
    setVisibleSections([])
    setVisibleMsgs([])
    setElapsed(0)

    // Real workspace-run dispatch — fires through the wrapper hook
    // which creates the sq_workspace_runs row via /api/workspace/run
    // and binds the canvas to the live render path through
    // useLiveRun(). Best-effort: errors are swallowed so the local
    // agent timeline still plays as visual feedback.
    if (onRequestRun) {
      try {
        const maybe = onRequestRun({ prompt: prompt.trim() || content.placeholder, mode })
        // The wrapper may return a Promise of (workspaceId, runId)
        // so the canvas can later show a "real run mounted" badge.
        // We don't await — the local timeline plays in parallel.
        if (maybe && typeof (maybe as Promise<unknown>).then === 'function') {
          ;(maybe as Promise<unknown>).catch(() => {
            // never break the local timeline because dispatch failed
          })
        }
      } catch {
        // never break the local timeline because dispatch failed
      }
    }

    const startedAt = Date.now()
    tickerRef.current = window.setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000))
    }, 500)

    content.sections.forEach((_, i) => {
      const id = window.setTimeout(() => {
        setVisibleSections((prev) => (prev.includes(i) ? prev : [...prev, i]))
      }, content.sections[i].delay)
      timeoutsRef.current.push(id)
    })

    content.agents.forEach((_, i) => {
      const id = window.setTimeout(() => {
        setVisibleMsgs((prev) => (prev.includes(i) ? prev : [...prev, i]))
      }, content.agents[i].t)
      timeoutsRef.current.push(id)
    })

    const lastDelay = Math.max(...content.sections.map((s) => s.delay))
    const doneId = window.setTimeout(() => {
      setBuildState('done')
      if (tickerRef.current) {
        window.clearInterval(tickerRef.current)
        tickerRef.current = null
      }
    }, lastDelay + 500)
    timeoutsRef.current.push(doneId)
  }

  function onPromptKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && buildState === 'idle' && prompt.trim()) {
      startBuild()
    }
  }

  // ----------------------------------------------------------------
  // Variant copy — QuamSpace vs Studio framing
  // ----------------------------------------------------------------

  const variantCopy =
    variant === 'studio'
      ? {
          chromeKicker: 'PROJECT · CONTENT STUDIO',
          chromeSub: 'project-scoped creation · runs · agents · outputs',
          standbyText: 'Agents standing by — every run is scoped to this project and audited.',
        }
      : {
          chromeKicker: 'OPERATOR · QUAMSPACE',
          chromeSub: 'workspace · drafts · notes · operator memory',
          standbyText: 'Agents standing by — drafts and notes save into your personal QuamSpace.',
        }

  // ----------------------------------------------------------------
  // Render helpers
  // ----------------------------------------------------------------

  function fmtTimer(s: number): string {
    const mm = Math.floor(s / 60).toString().padStart(2, '0')
    const ss = (s % 60).toString().padStart(2, '0')
    return `${mm}:${ss}`
  }

  // ----------------------------------------------------------------
  // Layout
  // ----------------------------------------------------------------

  const root: CSSProperties = {
    position: 'relative',
    background: '#08080c',
    border: '1px solid rgba(255,255,255,0.05)',
    borderRadius: 14,
    overflow: 'hidden',
    color: '#e4e8ee',
    fontFamily: 'var(--font-body, "SF Pro Display", Inter, system-ui, sans-serif)',
    display: 'grid',
    gridTemplateColumns: '1fr 250px',
    gridTemplateRows: '40px 38px 1fr 80px',
    gridTemplateAreas: `
      "chrome  agent"
      "modebar agent"
      "canvas  agent"
      "command agent"
    `,
    minHeight: 720,
    transition: 'border-color 300ms ease',
  }

  const gridBg: CSSProperties = {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    backgroundImage:
      'url("data:image/svg+xml;utf8,' +
      '<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2250%22 height=%2250%22>' +
      '<path d=%22M50 0H0v50%22 fill=%22none%22 stroke=%22%23ffffff%22 stroke-width=%221%22 stroke-opacity=%220.03%22/>' +
      '</svg>")',
    backgroundSize: '50px 50px',
    opacity: 1,
  }

  return (
    <div style={root}>
      <style>{`
        @keyframes qs-secIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes qs-agentIn { from { opacity: 0; transform: translateX(6px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes qs-pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.3; transform: scale(1.6); } }
        @keyframes qs-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
        @keyframes qs-spin { to { transform: rotate(360deg); } }
        .qs-section { animation: qs-secIn 450ms ease both; }
        .qs-agent-msg { animation: qs-agentIn 200ms ease both; }
        .qs-scroll::-webkit-scrollbar { width: 2px; }
        .qs-scroll::-webkit-scrollbar-track { background: transparent; }
        .qs-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.04); }
      `}</style>

      <div style={gridBg} />

      {/* Chrome bar */}
      <div
        style={{
          gridArea: 'chrome',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 14px',
          borderBottom: '1px solid rgba(255,255,255,0.04)',
          background: `linear-gradient(90deg, ${accent}0a, transparent 60%)`,
          position: 'relative',
          zIndex: 1,
          transition: 'background 300ms ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 22,
              height: 22,
              borderRadius: 6,
              background: `${accent}1f`,
              border: `1px solid ${accent}40`,
              display: 'grid',
              placeItems: 'center',
              color: accent,
              fontSize: 12,
              transition: 'all 300ms ease',
            }}
          >
            {modeDef.icon}
          </div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: accent,
              transition: 'color 300ms ease',
            }}
          >
            {modeDef.label}
          </div>
          <div style={{ fontSize: 10, color: '#4a5260', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            · {variantCopy.chromeKicker}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 10,
              color: '#8a94a0',
              fontVariantNumeric: 'tabular-nums',
              fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: buildState === 'building' ? '#dc2626' : '#3a4250',
                animation: buildState === 'building' ? 'qs-pulse 1s ease-in-out infinite' : undefined,
              }}
            />
            {fmtTimer(elapsed)}
          </div>

          {buildState === 'done' && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                onClick={() => alert('Export is a visual posture only — pipeline not wired. See WORKSPACE_SPEC.md.')}
                style={chromeBtn(accent)}
              >
                Export {modeDef.ext}
              </button>
              <button
                type="button"
                onClick={() => alert('PDF export is a visual posture only — pipeline not wired.')}
                style={chromeBtn(accent)}
              >
                Export .pdf
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mode bar */}
      <div
        style={{
          gridArea: 'modebar',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          padding: '0 14px',
          borderBottom: '1px solid rgba(255,255,255,0.04)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {MODES.map((m) => {
          const active = m.id === mode
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => setMode(m.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '5px 10px',
                borderRadius: 6,
                background: active ? `${m.color}1f` : 'transparent',
                border: `1px solid ${active ? `${m.color}40` : 'transparent'}`,
                color: active ? m.color : '#4a5260',
                fontSize: 10,
                fontWeight: active ? 700 : 500,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                transition: 'all 300ms ease',
                fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
              }}
            >
              <span style={{ fontSize: 11 }}>{m.icon}</span>
              {m.label}
              {active && (
                <span
                  style={{
                    fontSize: 8,
                    padding: '1px 4px',
                    borderRadius: 3,
                    background: `${m.color}29`,
                    color: m.color,
                    marginLeft: 2,
                  }}
                >
                  {m.ext}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Canvas */}
      <div
        ref={canvasScrollRef}
        className="qs-scroll"
        style={{
          gridArea: 'canvas',
          overflowY: 'auto',
          padding: '32px 24px 80px',
          position: 'relative',
          zIndex: 1,
          borderLeft: `1px solid ${accent}14`,
          transition: 'border-color 300ms ease',
        }}
      >
        <div style={{ maxWidth: 660, margin: '0 auto' }}>
          {buildState === 'idle' ? (
            <IdleState mode={modeDef} content={content} variantCopy={variantCopy} live={live} />
          ) : (
            <BuildingCanvas
              content={content}
              visible={visibleSections}
              accent={accent}
              buildState={buildState}
            />
          )}

          {liveRun && (liveRun.runId || liveRun.error) && (
            <LiveSectionsOverlay liveRun={liveRun} accent={accent} />
          )}

          {buildState === 'done' && (
            <DoneFooter
              mode={modeDef}
              content={content}
              variant={variant}
              onOpenInSiteBuilder={onOpenInSiteBuilder}
              onReset={resetState}
            />
          )}
        </div>
      </div>

      {/* Command bar */}
      <div
        style={{
          gridArea: 'command',
          padding: '10px 16px',
          borderTop: '1px solid rgba(255,255,255,0.04)',
          background: 'rgba(8,8,12,0.85)',
          backdropFilter: 'blur(12px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div style={{ display: 'flex', gap: 6 }}>
          {[
            { icon: '📎', label: 'File',     ready: false },
            { icon: '🎤', label: 'Voice',    ready: false },
            { icon: '🎥', label: 'Video',    ready: false },
            { icon: '🔗', label: 'URL',      ready: false },
            { icon: '📋', label: 'Template', ready: false },
          ].map((chip) => (
            <button
              key={chip.label}
              type="button"
              disabled={!chip.ready}
              title={chip.ready ? `Attach ${chip.label}` : `${chip.label} — coming soon`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '6px 12px',
                borderRadius: 8,
                background: chip.ready
                  ? 'rgba(139,92,246,0.08)'
                  : 'rgba(255,255,255,0.03)',
                border: chip.ready
                  ? '1px solid rgba(139,92,246,0.25)'
                  : '1px solid rgba(255,255,255,0.06)',
                color: chip.ready ? '#a78bfa' : '#3a3f4a',
                fontSize: 11,
                fontWeight: 600,
                cursor: chip.ready ? 'pointer' : 'default',
                fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                transition: 'all 0.15s ease',
                opacity: chip.ready ? 1 : 0.5,
              }}
            >
              <span style={{ fontSize: 14 }}>{chip.icon}</span> {chip.label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 12px',
              borderRadius: 8,
              background: 'rgba(255,255,255,0.02)',
              border: `1px solid ${accent}1f`,
              transition: 'border-color 300ms ease',
            }}
          >
            <span style={{ color: accent, fontSize: 12, transition: 'color 300ms ease' }}>✦</span>
            <input
              type="text"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={onPromptKey}
              placeholder={content.placeholder}
              disabled={buildState === 'building'}
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                outline: 'none',
                color: '#e4e8ee',
                fontSize: 12,
                fontWeight: 500,
                fontFamily: 'inherit',
              }}
            />
          </div>
          {buildState === 'done' ? (
            <button
              type="button"
              onClick={resetState}
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                background: 'rgba(5,150,105,0.15)',
                border: '1px solid rgba(5,150,105,0.4)',
                color: '#34d399',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
              }}
            >
              ✦ New
            </button>
          ) : buildState === 'building' ? (
            <button
              type="button"
              disabled
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                background: 'transparent',
                border: `1px solid ${accent}40`,
                color: accent,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
                cursor: 'not-allowed',
                transition: 'all 300ms ease',
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  border: `1.5px solid ${accent}40`,
                  borderTopColor: accent,
                  animation: 'qs-spin 800ms linear infinite',
                }}
              />
              Building
            </button>
          ) : (
            <button
              type="button"
              onClick={startBuild}
              style={{
                padding: '8px 18px',
                borderRadius: 8,
                background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
                border: 'none',
                color: '#fff',
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                boxShadow: `0 0 14px ${accent}40`,
                fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
                transition: 'all 300ms ease',
              }}
            >
              Run
            </button>
          )}
        </div>
      </div>

      {/* Agent stream */}
      <div
        style={{
          gridArea: 'agent',
          borderLeft: `1px solid ${accent}14`,
          background: 'rgba(8,8,12,0.6)',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          position: 'relative',
          zIndex: 1,
          transition: 'border-color 300ms ease',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '10px 12px',
            borderBottom: '1px solid rgba(255,255,255,0.04)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: buildState === 'building' ? accent : '#3a4250',
                animation: buildState === 'building' ? 'qs-pulse 1.5s ease-in-out infinite' : undefined,
                transition: 'background 300ms ease',
              }}
            />
            <span
              style={{
                fontSize: 8,
                fontWeight: 800,
                color: '#8a94a0',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
              }}
            >
              Agent Activity
            </span>
          </div>
          <span
            style={{
              fontSize: 8,
              color: '#4a5260',
              fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {visibleMsgs.length}/{content.agents.length}
          </span>
        </div>

        {/* Feed */}
        <div
          ref={agentScrollRef}
          className="qs-scroll"
          style={{ flex: 1, overflowY: 'auto', padding: '10px 12px', minHeight: 0 }}
        >
          {visibleMsgs.length === 0 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 120,
                opacity: 0.6,
              }}
            >
              <div style={{ fontSize: 28, color: accent, opacity: 0.2, marginBottom: 8 }}>{modeDef.icon}</div>
              <div
                style={{
                  fontSize: 9,
                  color: '#4a5260',
                  textAlign: 'center',
                  letterSpacing: '0.06em',
                }}
              >
                Agents standing by
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {visibleMsgs.map((i) => {
                const msg = content.agents[i]
                const color = AGENT_COLORS[msg.agent] || '#8a94a0'
                return (
                  <div key={i} className="qs-agent-msg" style={{ display: 'flex', gap: 6 }}>
                    <span style={{ color, fontSize: 10, lineHeight: 1.2 }}>▸</span>
                    <div>
                      <div style={{ fontSize: 8, fontWeight: 700, color, letterSpacing: '0.08em' }}>
                        {msg.agent}
                      </div>
                      <div style={{ fontSize: 9, color: '#8a94a0', marginTop: 1, lineHeight: 1.5 }}>
                        {msg.text}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer: Sources */}
        <div
          style={{
            padding: '8px 12px',
            borderTop: '1px solid rgba(255,255,255,0.04)',
          }}
        >
          <div
            style={{
              fontSize: 7,
              fontWeight: 800,
              color: '#4a5260',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              marginBottom: 5,
              fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
            }}
          >
            Sources
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {ALL_SOURCES.map((s) => (
              <span
                key={s}
                style={{
                  fontSize: 7,
                  padding: '2px 5px',
                  borderRadius: 3,
                  background: 'rgba(255,255,255,0.025)',
                  color: '#6a7480',
                  border: '1px solid rgba(255,255,255,0.04)',
                  fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
                }}
              >
                {s}
              </span>
            ))}
          </div>
        </div>

        {/* Footer: Agents */}
        <div style={{ padding: '8px 12px', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
          <div
            style={{
              fontSize: 7,
              fontWeight: 800,
              color: '#4a5260',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              marginBottom: 5,
              fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
            }}
          >
            Agents
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {ALL_AGENTS.map((a) => {
              const color = AGENT_COLORS[a]
              return (
                <span
                  key={a}
                  style={{
                    fontSize: 7,
                    padding: '2px 5px',
                    borderRadius: 3,
                    background: `${color}14`,
                    color,
                    border: `1px solid ${color}29`,
                    fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
                    fontWeight: 700,
                  }}
                >
                  {a}
                </span>
              )
            })}
          </div>
        </div>

        {/* Live integration footer (honest) */}
        {live && (
          <div
            style={{
              padding: '8px 12px',
              borderTop: '1px solid rgba(255,255,255,0.04)',
              fontSize: 8,
              color: '#4a5260',
              fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
              lineHeight: 1.6,
            }}
          >
            <div style={{ marginBottom: 3 }}>
              live · runs {live.runsCount ?? 0} · events {live.agentEventsCount ?? 0}
            </div>
            {live.latestRunMode && (
              <div>
                latest {live.latestRunMode} · {live.latestRunStatus}
              </div>
            )}
            {live.error && <div style={{ color: '#dc2626', marginTop: 3 }}>{live.error}</div>}
          </div>
        )}
      </div>
    </div>
  )
}

// ----------------------------------------------------------------
// Idle state
// ----------------------------------------------------------------

function IdleState(props: {
  mode: ModeDef
  content: ModeContent
  variantCopy: { standbyText: string }
  live?: CanvasLiveContext
}) {
  const { mode, content, variantCopy, live } = props
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 480,
        textAlign: 'center',
        gap: 16,
      }}
    >
      <div style={{ fontSize: 96, color: mode.color, opacity: 0.2 }}>{mode.icon}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: '#e4e8ee' }}>{content.emptyTitle}</div>
      <div style={{ fontSize: 12, color: '#8a94a0', maxWidth: 420, lineHeight: 1.7 }}>{content.emptyBody}</div>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 12px',
          borderRadius: 16,
          background: `${mode.color}14`,
          border: `1px solid ${mode.color}29`,
          color: mode.color,
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
        }}
      >
        ✦ {variantCopy.standbyText}
      </div>
      {live && (live.runsCount || live.agentEventsCount) ? (
        <div
          style={{
            fontSize: 10,
            color: '#4a5260',
            fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
            marginTop: 6,
          }}
        >
          live history: {live.runsCount ?? 0} runs · {live.agentEventsCount ?? 0} agent events
        </div>
      ) : null}
    </div>
  )
}

// ----------------------------------------------------------------
// Building canvas — section renderers
// ----------------------------------------------------------------

function BuildingCanvas(props: {
  content: ModeContent
  visible: number[]
  accent: string
  buildState: BuildState
}) {
  const { content, visible, accent, buildState } = props
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {visible.map((i) => {
        const section = content.sections[i]
        return <SectionRenderer key={i} section={section} accent={accent} />
      })}
      {buildState === 'building' && (
        <span
          style={{
            display: 'inline-block',
            width: 8,
            height: 14,
            background: accent,
            animation: 'qs-blink 1s step-end infinite',
            marginTop: 4,
          }}
        />
      )}
    </div>
  )
}

function SectionRenderer({ section, accent }: { section: SectionBase; accent: string }) {
  const agentColor = section.color || accent
  const wrap = (children: React.ReactNode, withAgentBorder = false): CSSProperties => ({
    paddingLeft: withAgentBorder && section.agent ? 12 : 0,
    borderLeft: withAgentBorder && section.agent ? `2px solid ${agentColor}40` : undefined,
  })

  const agentTag = section.agent ? (
    <div
      style={{
        fontSize: 8,
        fontWeight: 700,
        color: agentColor,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        marginBottom: 4,
        fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
      }}
    >
      ▸ {section.agent}
    </div>
  ) : null

  switch (section.type) {
    case 'title':
      return (
        <div className="qs-section" style={{ fontSize: 22, fontWeight: 800, color: '#e4e8ee', letterSpacing: '-0.01em' }}>
          {section.text}
        </div>
      )
    case 'meta':
      return (
        <div className="qs-section" style={{ fontSize: 10, color: '#4a5260', fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)', letterSpacing: '0.06em' }}>
          {section.text}
        </div>
      )
    case 'divider':
      return <div className="qs-section" style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '6px 0' }} />
    case 'heading':
      return (
        <div
          className="qs-section"
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: '#c8d0da',
            marginTop: 22,
            letterSpacing: '-0.005em',
          }}
        >
          {section.text}
        </div>
      )
    case 'body':
      return (
        <div className="qs-section" style={wrap(null, true)}>
          {agentTag}
          <div style={{ fontSize: 12.5, color: '#8a94a0', lineHeight: 1.85 }}>{section.text}</div>
        </div>
      )
    case 'security':
      return (
        <div
          className="qs-section"
          style={{
            background: 'rgba(220,38,38,0.06)',
            border: '1px solid rgba(220,38,38,0.2)',
            borderRadius: 8,
            padding: '12px 14px',
          }}
        >
          <div
            style={{
              fontSize: 8,
              fontWeight: 800,
              color: '#dc2626',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              marginBottom: 6,
              fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
            }}
          >
            ▸ Security · {section.agent}
          </div>
          <div style={{ fontSize: 12, color: '#c8d0da', lineHeight: 1.7 }}>{section.text}</div>
        </div>
      )
    case 'callout':
      return (
        <div
          className="qs-section"
          style={{
            background: `${agentColor}14`,
            borderLeft: `3px solid ${agentColor}`,
            borderRadius: 4,
            padding: '12px 14px',
          }}
        >
          <div
            style={{
              fontSize: 8,
              fontWeight: 800,
              color: agentColor,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              marginBottom: 6,
              fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
            }}
          >
            Recommendation · {section.agent}
          </div>
          <div style={{ fontSize: 12, color: '#c8d0da', lineHeight: 1.7 }}>{section.text}</div>
        </div>
      )
    case 'stats':
      return (
        <div className="qs-section" style={wrap(null, true)}>
          {agentTag}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            {(section.stats || []).map((s, idx) => (
              <div
                key={idx}
                style={{
                  background: `${agentColor}14`,
                  border: `1px solid ${agentColor}29`,
                  borderRadius: 8,
                  padding: '12px 8px',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: 18, fontWeight: 800, color: agentColor, fontVariantNumeric: 'tabular-nums' }}>{s.value}</div>
                <div
                  style={{
                    fontSize: 7,
                    color: '#6a7480',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    marginTop: 4,
                    fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
                  }}
                >
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    case 'extraction':
      return (
        <div className="qs-section" style={wrap(null, true)}>
          {agentTag}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {(section.items as ExtractionItem[]).map((item, idx) => (
              <div
                key={idx}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '50px 1fr auto',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 12px',
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.04)',
                  borderRadius: 6,
                }}
              >
                <span style={{ fontSize: 10, color: '#4a5260', fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)' }}>
                  {item.page}
                </span>
                <div>
                  <div style={{ fontSize: 12, color: '#c8d0da' }}>{item.label}</div>
                  {item.detail && <div style={{ fontSize: 10, color: '#6a7480', marginTop: 2 }}>{item.detail}</div>}
                </div>
                <span style={extractionBadge(item.status)}>
                  {item.status === 'pass' && '✓ Pass'}
                  {item.status === 'concern' && '⚠ Concern'}
                  {item.status === 'fail' && '✗ Fail'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )
    case 'slide-title':
      return (
        <div
          className="qs-section"
          style={{
            background: `linear-gradient(135deg, ${accent}29, ${accent}05)`,
            border: `1px solid ${accent}40`,
            borderRadius: 12,
            padding: '28px 24px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 8, color: accent, letterSpacing: '0.18em', fontWeight: 800, marginBottom: 10, textTransform: 'uppercase' }}>SLIDE 1</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#e4e8ee' }}>{section.text}</div>
        </div>
      )
    case 'slide':
      return (
        <div
          className="qs-section"
          style={{
            background: 'rgba(255,255,255,0.025)',
            border: '1px solid rgba(255,255,255,0.05)',
            borderRadius: 10,
            padding: '18px 20px',
          }}
        >
          <div style={{ fontSize: 8, color: accent, letterSpacing: '0.18em', fontWeight: 800, marginBottom: 8, textTransform: 'uppercase' }}>
            SLIDE
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#e4e8ee', marginBottom: 12 }}>{section.text}</div>
          <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {(section.bullets || []).map((b, idx) => (
              <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: '#8a94a0' }}>
                <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#dc2626', marginTop: 7, flexShrink: 0 }} />
                {b.text}
              </li>
            ))}
          </ul>
        </div>
      )
    case 'slide-stats':
      return (
        <div className="qs-section" style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 10, padding: '18px 20px' }}>
          <div style={{ fontSize: 8, color: accent, letterSpacing: '0.18em', fontWeight: 800, marginBottom: 8, textTransform: 'uppercase' }}>SLIDE</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#e4e8ee', marginBottom: 14 }}>{section.text}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            {(section.stats || []).map((s, idx) => (
              <div key={idx} style={{ textAlign: 'center', padding: '10px 6px', background: 'rgba(255,255,255,0.025)', borderRadius: 6 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#e4e8ee' }}>{s.value}</div>
                <div style={{ fontSize: 7, color: '#6a7480', letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      )
    case 'slide-chart': {
      const max = Math.max(...(section.bars || []).map((b) => Math.max(b.actual, b.projected)))
      return (
        <div className="qs-section" style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 10, padding: '18px 20px' }}>
          <div style={{ fontSize: 8, color: accent, letterSpacing: '0.18em', fontWeight: 800, marginBottom: 8, textTransform: 'uppercase' }}>SLIDE</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#e4e8ee', marginBottom: 14 }}>{section.text}</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, height: 140, marginBottom: 8 }}>
            {(section.bars || []).map((bar, idx) => (
              <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, height: '100%' }}>
                <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', width: '100%', justifyContent: 'center', gap: 3 }}>
                  {bar.actual > 0 && (
                    <div style={{ width: 14, background: '#059669', height: `${(bar.actual / max) * 100}%`, borderRadius: '2px 2px 0 0' }} />
                  )}
                  {bar.projected > 0 && (
                    <div
                      style={{
                        width: 14,
                        border: '1.5px dashed #f59e0b',
                        background: 'rgba(245,158,11,0.08)',
                        height: `${(bar.projected / max) * 100}%`,
                        borderRadius: '2px 2px 0 0',
                      }}
                    />
                  )}
                </div>
                <div style={{ fontSize: 9, color: '#6a7480', fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)' }}>{bar.label}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 12, fontSize: 8, color: '#6a7480', fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            <span><span style={{ display: 'inline-block', width: 8, height: 8, background: '#059669', marginRight: 4 }} />Actual</span>
            <span><span style={{ display: 'inline-block', width: 8, height: 8, border: '1.5px dashed #f59e0b', marginRight: 4 }} />Projected</span>
          </div>
        </div>
      )
    }
    case 'slide-ask': {
      const ask = section.ask!
      return (
        <div className="qs-section" style={{ background: `linear-gradient(135deg, ${accent}1f, ${accent}05)`, border: `1px solid ${accent}40`, borderRadius: 12, padding: '24px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 8, color: accent, letterSpacing: '0.18em', fontWeight: 800, marginBottom: 8, textTransform: 'uppercase' }}>SLIDE</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#c8d0da', marginBottom: 12 }}>{section.text}</div>
          <div style={{ fontSize: 36, fontWeight: 900, color: '#e4e8ee', marginBottom: 16, fontVariantNumeric: 'tabular-nums' }}>{ask.amount}</div>
          <div style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', marginBottom: 8 }}>
            {ask.segments.map((seg, idx) => (
              <div key={idx} style={{ width: `${seg.pct}%`, background: seg.color }} />
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-around', fontSize: 9, color: '#8a94a0', fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)' }}>
            {ask.segments.map((seg) => (
              <span key={seg.label}>
                <span style={{ color: seg.color, fontWeight: 700 }}>{seg.pct}%</span> {seg.label}
              </span>
            ))}
          </div>
        </div>
      )
    }
    case 'spreadsheet-table':
      return (
        <div className="qs-section" style={wrap(null, true)}>
          {agentTag}
          <div style={{ overflowX: 'auto', border: '1px solid rgba(255,255,255,0.04)', borderRadius: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontVariantNumeric: 'tabular-nums', fontSize: 11 }}>
              <thead>
                <tr style={{ background: 'rgba(255,255,255,0.025)' }}>
                  {section.table!.headers.map((h, idx) => (
                    <th
                      key={idx}
                      style={{
                        textAlign: idx === 0 ? 'left' : 'right',
                        padding: '8px 12px',
                        fontSize: 8,
                        color: '#6a7480',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
                        borderBottom: '1px solid rgba(255,255,255,0.04)',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {section.table!.rows.map((row, rIdx) => (
                  <tr key={rIdx} style={{ background: rIdx % 2 ? 'rgba(255,255,255,0.012)' : 'transparent' }}>
                    {row.map((cell, cIdx) => {
                      const text = typeof cell === 'string' ? cell : cell.value
                      const isKey = typeof cell !== 'string' && cell.key
                      return (
                        <td
                          key={cIdx}
                          style={{
                            padding: '7px 12px',
                            textAlign: cIdx === 0 ? 'left' : 'right',
                            color: isKey ? '#34d399' : '#c8d0da',
                            fontWeight: isKey ? 700 : 500,
                            borderBottom: '1px solid rgba(255,255,255,0.03)',
                          }}
                        >
                          {text}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )
    case 'note-tag':
      return (
        <div className="qs-section" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {(section.tags || []).map((t) => (
            <span
              key={t}
              style={{
                fontSize: 9,
                padding: '3px 8px',
                borderRadius: 12,
                background: 'rgba(245,158,11,0.12)',
                color: '#f59e0b',
                border: '1px solid rgba(245,158,11,0.25)',
                fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
              }}
            >
              #{t}
            </span>
          ))}
        </div>
      )
    case 'note-item':
      return (
        <div className="qs-section" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {(section.noteItems || []).map((n, idx) => (
            <div
              key={idx}
              style={{
                display: 'grid',
                gridTemplateColumns: '50px 1fr auto',
                gap: 12,
                padding: '8px 12px',
                background: 'rgba(255,255,255,0.02)',
                borderRadius: 6,
                alignItems: 'center',
              }}
            >
              <span style={{ fontSize: 10, color: '#4a5260', fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)' }}>{n.time}</span>
              <span style={{ fontSize: 12, color: '#c8d0da' }}>{n.text}</span>
              <span style={priorityBadge(n.priority)}>
                ● {n.priority.toUpperCase()}
              </span>
            </div>
          ))}
        </div>
      )
    case 'action-items':
      return (
        <div className="qs-section" style={wrap(null, true)}>
          {agentTag}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {(section.actionItems || []).map((a, idx) => {
              const sCol = actionStatusColor(a.status)
              return (
                <div
                  key={idx}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '24px 1fr auto',
                    gap: 12,
                    padding: '10px 12px',
                    background: 'rgba(255,255,255,0.02)',
                    border: `1px solid ${sCol}29`,
                    borderRadius: 6,
                    alignItems: 'center',
                  }}
                >
                  <div
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 3,
                      border: `1.5px solid ${sCol}`,
                      display: 'grid',
                      placeItems: 'center',
                      color: sCol,
                      fontSize: 9,
                    }}
                  >
                    {a.status === 'auto' ? '✦' : ''}
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: '#c8d0da' }}>{a.text}</div>
                    <div style={{ fontSize: 9, color: '#6a7480', marginTop: 2, fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)' }}>
                      {a.owner} · due {a.due}
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: 7,
                      padding: '2px 6px',
                      borderRadius: 3,
                      background: `${sCol}1f`,
                      color: sCol,
                      fontWeight: 800,
                      letterSpacing: '0.1em',
                      fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
                    }}
                  >
                    {a.status.toUpperCase()}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )
    // -------- Build mode renderers --------
    case 'page-outline':
      return (
        <div className="qs-section" style={wrap(null, true)}>
          {agentTag}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {(section.items || []).map((p: any, idx: number) => (
              <div
                key={idx}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '120px 1fr auto',
                  gap: 12,
                  padding: '8px 12px',
                  background: 'rgba(20,184,166,0.05)',
                  border: '1px solid rgba(20,184,166,0.18)',
                  borderRadius: 6,
                }}
              >
                <span style={{ fontSize: 11, color: '#14b8a6', fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)' }}>{p.path}</span>
                <span style={{ fontSize: 12, color: '#c8d0da' }}>{p.title}</span>
                <span style={{ fontSize: 8, color: '#6a7480', letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)' }}>
                  {p.blocks} blocks
                </span>
              </div>
            ))}
          </div>
        </div>
      )
    case 'hero-preview':
      return (
        <div className="qs-section" style={wrap(null, true)}>
          {agentTag}
          <div
            style={{
              padding: '28px 24px',
              background: 'linear-gradient(135deg, rgba(20,184,166,0.18), rgba(20,184,166,0.04))',
              border: '1px solid rgba(20,184,166,0.3)',
              borderRadius: 12,
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 22, fontWeight: 800, color: '#e4e8ee', marginBottom: 14 }}>{section.text}</div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
              {(section.items as string[] || []).map((it, idx) => (
                <span
                  key={idx}
                  style={{
                    fontSize: 9,
                    padding: '4px 10px',
                    borderRadius: 12,
                    background: 'rgba(20,184,166,0.14)',
                    color: '#14b8a6',
                    fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
                    fontWeight: 700,
                  }}
                >
                  {it}
                </span>
              ))}
            </div>
          </div>
        </div>
      )
    case 'feature-grid':
      return (
        <div className="qs-section" style={wrap(null, true)}>
          {agentTag}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
            {(section.items || []).map((f: any, idx: number) => (
              <div
                key={idx}
                style={{
                  padding: '14px 14px',
                  background: 'rgba(255,255,255,0.025)',
                  border: '1px solid rgba(255,255,255,0.05)',
                  borderRadius: 8,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 700, color: '#14b8a6', marginBottom: 4 }}>{f.title}</div>
                <div style={{ fontSize: 11, color: '#8a94a0', lineHeight: 1.6 }}>{f.body}</div>
              </div>
            ))}
          </div>
        </div>
      )
    case 'style-direction':
      return (
        <div className="qs-section" style={wrap(null, true)}>
          {agentTag}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {(section.items || []).map((s: any, idx: number) => (
              <div
                key={idx}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '120px 1fr',
                  gap: 12,
                  padding: '6px 12px',
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                }}
              >
                <span style={{ fontSize: 8, color: '#6a7480', textTransform: 'uppercase', letterSpacing: '0.12em', fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)' }}>
                  {s.label}
                </span>
                <span style={{ fontSize: 11, color: '#c8d0da' }}>{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      )
    case 'cta-block':
      return (
        <div
          className="qs-section"
          style={{
            background: 'rgba(20,184,166,0.08)',
            border: '1px dashed rgba(20,184,166,0.4)',
            borderRadius: 10,
            padding: '20px 18px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 800, color: '#14b8a6', marginBottom: 6 }}>{section.text}</div>
          <div style={{ fontSize: 11, color: '#8a94a0' }}>{(section as any).subtext}</div>
        </div>
      )
    default:
      return null
  }
}

// ----------------------------------------------------------------
// Done footer (incl. Site Builder handoff for Build mode)
// ----------------------------------------------------------------

function DoneFooter(props: {
  mode: ModeDef
  content: ModeContent
  variant: CanvasVariant
  onOpenInSiteBuilder?: () => void
  onReset: () => void
}) {
  const { mode, content, variant, onOpenInSiteBuilder } = props
  const sectionCount = content.sections.length
  const agentCount = new Set(content.agents.map((a) => a.agent)).size

  const isBuildMode = mode.id === 'build'

  return (
    <div style={{ marginTop: 32 }}>
      <div
        style={{
          padding: '14px 16px',
          background: 'rgba(5,150,105,0.08)',
          border: '1px solid rgba(5,150,105,0.3)',
          borderRadius: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color: '#34d399', fontSize: 14 }}>✦</span>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#34d399' }}>Complete</div>
            <div style={{ fontSize: 10, color: '#6a7480', marginTop: 2, fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)' }}>
              {agentCount} agents · {sectionCount} sections
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <span style={exportFormatChip()}>{mode.ext}</span>
          <span style={exportFormatChip()}>.pdf</span>
        </div>
      </div>

      {isBuildMode && (
        <div
          style={{
            marginTop: 12,
            padding: '14px 16px',
            background: 'rgba(20,184,166,0.06)',
            border: '1px dashed rgba(20,184,166,0.35)',
            borderRadius: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, color: '#14b8a6', textTransform: 'uppercase', letterSpacing: '0.12em', fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)' }}>
                Build · Site Builder Handoff
              </div>
              <div style={{ fontSize: 11, color: '#8a94a0', marginTop: 4, lineHeight: 1.6, maxWidth: 460 }}>
                Build mode produced a draft site outline. Site Builder is the manual visual construction layer.
                {variant === 'studio'
                  ? ' Handoff persists a real sq_sites row + Home page + starter blocks (idempotent — existing sites and blocks are never clobbered).'
                  : ' Site Builder requires project context — open this seam from a Studio surface.'}
              </div>
            </div>
            {onOpenInSiteBuilder ? (
              <button
                type="button"
                onClick={onOpenInSiteBuilder}
                style={{
                  padding: '8px 14px',
                  borderRadius: 8,
                  background: 'rgba(20,184,166,0.18)',
                  border: '1px solid rgba(20,184,166,0.45)',
                  color: '#14b8a6',
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
                  whiteSpace: 'nowrap',
                }}
              >
                Open in Site Builder →
              </button>
            ) : (
              <button
                type="button"
                disabled
                title="Site Builder requires project context. Open Build mode from a project Studio."
                style={{
                  padding: '8px 14px',
                  borderRadius: 8,
                  background: 'rgba(255,255,255,0.025)',
                  border: '1px dashed rgba(255,255,255,0.1)',
                  color: '#4a5260',
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  cursor: 'not-allowed',
                  fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
                  whiteSpace: 'nowrap',
                }}
              >
                Save as Site Draft (project required)
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ----------------------------------------------------------------
// Style helpers
// ----------------------------------------------------------------

function chromeBtn(accent: string): CSSProperties {
  return {
    fontSize: 9,
    padding: '4px 10px',
    borderRadius: 5,
    background: `${accent}14`,
    border: `1px solid ${accent}29`,
    color: accent,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
  }
}

function extractionBadge(status: 'pass' | 'concern' | 'fail'): CSSProperties {
  const map = {
    pass: { bg: 'rgba(5,150,105,0.14)', col: '#34d399' },
    concern: { bg: 'rgba(245,158,11,0.14)', col: '#fbbf24' },
    fail: { bg: 'rgba(220,38,38,0.14)', col: '#f87171' },
  }
  const m = map[status]
  return {
    fontSize: 8,
    padding: '3px 7px',
    borderRadius: 3,
    background: m.bg,
    color: m.col,
    fontWeight: 800,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
    whiteSpace: 'nowrap',
  }
}

function priorityBadge(priority: 'high' | 'med' | 'low'): CSSProperties {
  const map = {
    high: '#dc2626',
    med:  '#f59e0b',
    low:  '#059669',
  }
  return {
    fontSize: 7,
    color: map[priority],
    fontWeight: 800,
    letterSpacing: '0.1em',
    fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
    whiteSpace: 'nowrap',
  }
}

function actionStatusColor(s: ActionItem['status']): string {
  if (s === 'urgent') return '#dc2626'
  if (s === 'pending') return '#f59e0b'
  if (s === 'auto') return '#059669'
  return '#f59e0b'
}

function exportFormatChip(): CSSProperties {
  return {
    fontSize: 8,
    padding: '3px 8px',
    borderRadius: 3,
    background: 'rgba(255,255,255,0.04)',
    color: '#8a94a0',
    fontWeight: 700,
    letterSpacing: '0.1em',
    fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
  }
}

// ----------------------------------------------------------------
// LiveSectionsOverlay — renders the real persisted sections from
// sq_workspace_sections (filtered by run_id) when the wrapper has
// supplied a liveRun binding. This sits beneath the local agent
// timeline so the operator sees both the demo motion and the real
// persisted truth side-by-side.
//
// When the run terminal-states (done / error / cancelled) the
// overlay carries the badge color so the operator knows truth has
// landed without scrolling out of the canvas.
// ----------------------------------------------------------------

function LiveSectionsOverlay(props: {
  liveRun: NonNullable<QuamSpaceCanvasProps['liveRun']>
  accent: string
}) {
  const { liveRun, accent } = props
  const status = liveRun.status || 'pending'
  const sevColor =
    status === 'done'
      ? '#059669'
      : status === 'error'
        ? '#dc2626'
        : status === 'cancelled'
          ? '#f59e0b'
          : accent

  const containerStyle: CSSProperties = {
    marginTop: 32,
    padding: '18px 20px',
    borderRadius: 12,
    border: `1px solid ${sevColor}33`,
    background: 'rgba(255,255,255,0.018)',
  }

  const headerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
    fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
    fontSize: 9,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: '#7f8db4',
  }

  const meta: string[] = []
  if (liveRun.runId) meta.push(`run ${liveRun.runId.slice(0, 8)}`)
  if (liveRun.mode) meta.push(liveRun.mode)
  if (liveRun.provider) meta.push(liveRun.provider)
  meta.push(status)

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <span style={{ color: sevColor, fontWeight: 700 }}>
          ● LIVE · sq_workspace_sections
        </span>
        <span>{meta.join(' · ')}</span>
      </div>

      {liveRun.error && (
        <div
          style={{
            padding: '10px 12px',
            borderRadius: 8,
            background: 'rgba(220,38,38,0.08)',
            border: '1px solid rgba(220,38,38,0.32)',
            color: '#fca5a5',
            fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
            fontSize: 11,
            marginBottom: 12,
          }}
        >
          {liveRun.error}
        </div>
      )}

      {liveRun.sections.length === 0 ? (
        <div
          style={{
            fontSize: 12,
            color: '#7f8db4',
            fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
          }}
        >
          {status === 'done'
            ? 'Run completed with no persisted sections.'
            : status === 'error'
              ? 'Run failed before any sections were committed.'
              : 'Awaiting first persisted section…'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {liveRun.sections.map((s) => (
            <LiveSectionCard key={s.id} section={s} accent={accent} />
          ))}
        </div>
      )}

      {liveRun.events.length > 0 && (
        <div
          style={{
            marginTop: 14,
            paddingTop: 12,
            borderTop: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <div
            style={{
              fontSize: 9,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: '#7f8db4',
              marginBottom: 8,
              fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
            }}
          >
            Agent activity ({liveRun.events.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {liveRun.events.slice(-6).map((e) => (
              <div
                key={e.id}
                style={{
                  fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
                  fontSize: 10,
                  color: '#9aa4b0',
                }}
              >
                <span style={{ color: accent }}>{e.agent_name}</span>
                {' · '}
                <span style={{ color: '#7f8db4' }}>{e.event_type}</span>
                {' — '}
                {e.message}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function LiveSectionCard(props: {
  section: NonNullable<QuamSpaceCanvasProps['liveRun']>['sections'][number]
  accent: string
}) {
  const { section, accent } = props
  const text =
    typeof section.content_json?.text === 'string'
      ? (section.content_json.text as string)
      : ''
  return (
    <div
      style={{
        padding: '14px 16px',
        borderRadius: 10,
        background: 'rgba(255,255,255,0.025)',
        border: '1px solid rgba(255,255,255,0.05)',
      }}
    >
      <div
        style={{
          fontSize: 8,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: '#7f8db4',
          marginBottom: 6,
          fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
        }}
      >
        {section.section_type}
        {section.source_agent ? ` · ${section.source_agent}` : ''}
      </div>
      {section.title && (
        <div style={{ fontSize: 14, fontWeight: 700, color: '#edf2ff', marginBottom: 6 }}>
          {section.title}
        </div>
      )}
      {text && (
        <div style={{ fontSize: 12, color: '#cbd5ff', lineHeight: 1.6 }}>{text}</div>
      )}
      {!text && !section.title && (
        <div
          style={{
            fontSize: 10,
            color: '#7f8db4',
            fontFamily: 'var(--font-mono, "SF Mono", ui-monospace, monospace)',
          }}
        >
          {Object.keys(section.content_json || {}).join(', ') || '(empty payload)'}
        </div>
      )}
    </div>
  )
}
